/**
 * A small ring drawn from the same geometry as the real thing: twelve month
 * arcs, a tick per day, and an optional mark on one date. It is the app's
 * identity mark, and on the collect form it shows the date being entered.
 */
import { MONTH_NAMES, dateToAngle, daysInMonth, monthArc, polarToXY } from '@circle/shared';

const NS = 'http://www.w3.org/2000/svg';

export interface RingSealOptions {
  size?: number;
  year?: number;
  /** Highlighted date, if any. */
  mark?: { month: number; day: number } | null;
  label?: boolean;
}

export class RingSeal {
  readonly root: SVGSVGElement;
  private readonly markGroup: SVGGElement;
  private readonly labelText: SVGTextElement;
  private readonly year: number;
  private readonly radius: number;

  constructor(options: RingSealOptions = {}) {
    const size = options.size ?? 108;
    this.year = options.year ?? new Date().getFullYear();
    this.radius = size / 2 - 9;

    this.root = document.createElementNS(NS, 'svg');
    this.root.setAttribute('class', 'seal');
    this.root.setAttribute('width', String(size));
    this.root.setAttribute('height', String(size));
    this.root.setAttribute('viewBox', `${-size / 2} ${-size / 2} ${size} ${size}`);
    this.root.setAttribute('aria-hidden', 'true');

    const ring = document.createElementNS(NS, 'circle');
    ring.setAttribute('r', String(this.radius));
    ring.setAttribute('fill', 'none');
    ring.setAttribute('stroke', 'var(--ui-line-strong)');
    ring.setAttribute('stroke-width', '1');
    this.root.appendChild(ring);

    // A day tick every 5 days: at this size 365 of them would merge into the
    // ring line and read as no graduation at all.
    const step = this.radius > 90 ? 1 : 5;
    const ticks = document.createElementNS(NS, 'g');
    for (let m = 1; m <= 12; m++) {
      for (let d = 1; d <= daysInMonth(this.year, m); d += step) {
        ticks.appendChild(
          this.radial(
            dateToAngle(this.year, m, d),
            this.radius - (d === 1 ? 7 : 3.5),
            this.radius,
            d === 1 ? 'var(--ui-dim)' : 'var(--ui-line-strong)',
          ),
        );
      }
    }
    this.root.appendChild(ticks);

    // A hairline where each month begins, so the twelve arcs read as arcs.
    for (let m = 1; m <= 12; m++) {
      const arc = monthArc(this.year, m);
      this.root.appendChild(this.radial(arc.start, this.radius - 9, this.radius + 3, 'var(--ui-line)'));
    }

    this.markGroup = document.createElementNS(NS, 'g');
    this.root.appendChild(this.markGroup);

    this.labelText = document.createElementNS(NS, 'text');
    this.labelText.setAttribute('text-anchor', 'middle');
    this.labelText.setAttribute('dominant-baseline', 'central');
    this.labelText.setAttribute('font-size', '12');
    this.labelText.setAttribute('fill', 'var(--ui-dim)');
    this.labelText.setAttribute('font-family', 'inherit');
    if (options.label !== false) this.root.appendChild(this.labelText);

    this.setMark(options.mark ?? null);
  }

  setMark(mark: { month: number; day: number } | null): void {
    while (this.markGroup.firstChild) this.markGroup.removeChild(this.markGroup.firstChild);
    if (!mark || !isRealDate(this.year, mark.month, mark.day)) {
      this.labelText.textContent = '';
      return;
    }
    const angle = dateToAngle(this.year, mark.month, mark.day);
    const dot = document.createElementNS(NS, 'circle');
    const { x, y } = polarToXY(angle, this.radius);
    dot.setAttribute('cx', x.toFixed(2));
    dot.setAttribute('cy', y.toFixed(2));
    dot.setAttribute('r', '3.4');
    dot.setAttribute('fill', 'var(--ui-brass)');
    this.markGroup.appendChild(dot);
    this.labelText.textContent = `${mark.day} ${MONTH_NAMES[mark.month - 1]?.slice(0, 3) ?? ''}`;
  }

  private radial(angle: number, r0: number, r1: number, stroke: string): SVGLineElement {
    const line = document.createElementNS(NS, 'line');
    const inner = polarToXY(angle, r0);
    const outer = polarToXY(angle, r1);
    line.setAttribute('x1', inner.x.toFixed(2));
    line.setAttribute('y1', inner.y.toFixed(2));
    line.setAttribute('x2', outer.x.toFixed(2));
    line.setAttribute('y2', outer.y.toFixed(2));
    line.setAttribute('stroke', stroke);
    line.setAttribute('stroke-width', '1');
    return line;
  }
}

function isRealDate(year: number, month: number, day: number): boolean {
  return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
}
